import React, { useCallback } from 'react';
import { TableRow, TableCell, Checkbox, Typography, IconButton, Tooltip } from '@mui/material';
import { Science as ScienceIcon, RateReview as RateReviewIcon } from '@mui/icons-material';
import { BatchWorkflowListItem, WorkflowStatusCodes } from '../../../types/workflow';
import {
  RENDER_AFFECTING_BATCH_FIELDS,
  type WorkflowColumnDef,
} from '../workflowColumns';

interface BatchWorkflowRowProps {
  batch: BatchWorkflowListItem;
  selected: boolean;
  onToggleSelect: () => void;
  onClick: () => void;
  onOpenWorkspace?: (batch: BatchWorkflowListItem) => void;
  onReviewSamples?: (batch: BatchWorkflowListItem) => void;
  reviewingBatchId?: number | null;
  showActions?: boolean;
  showCheckbox?: boolean;
  /** Ordered, visible columns to render between the fixed ID and Actions cells. Must be a
   *  referentially-stable array (memoized by the parent) for the row memo to work. */
  columns: WorkflowColumnDef[];
}

const BatchWorkflowRowComponent: React.FC<BatchWorkflowRowProps> = ({
  batch, selected, onToggleSelect, onClick,
  onOpenWorkspace, onReviewSamples, reviewingBatchId, showActions, showCheckbox = true, columns,
}) => {
  const handleCheckboxClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleSelect();
  }, [onToggleSelect]);

  return (
    <TableRow hover selected={selected} onClick={onClick} sx={{ cursor: 'pointer' }}>
      {showCheckbox && (
        <TableCell padding="checkbox" onClick={handleCheckboxClick}>
          <Checkbox checked={selected} aria-label={`Select batch ${batch.batchNumber}`} />
        </TableCell>
      )}
      <TableCell>
        <Typography variant="body2" fontWeight={500}>{batch.id}</Typography>
      </TableCell>
      {columns.map((col) => (
        <TableCell key={col.key} align={col.align}>
          {col.renderCell(batch)}
        </TableCell>
      ))}
      {showActions && (
        <TableCell onClick={(e) => e.stopPropagation()}>
          {(batch.batchId || batch.soilBatchId) && (
            <Tooltip title="Open Workspace">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onOpenWorkspace?.(batch); }}
                aria-label="Open Workspace"
              >
                <ScienceIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {batch.batchId && batch.statusCode === WorkflowStatusCodes.AWAITING_REVIEW && (
            <Tooltip title="Review Samples">
              <span>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); onReviewSamples?.(batch); }}
                  disabled={reviewingBatchId === batch.id}
                  aria-label="Review Samples"
                >
                  <RateReviewIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </TableCell>
      )}
      <TableCell padding="checkbox" />
    </TableRow>
  );
};

export const BatchWorkflowRow = React.memo<BatchWorkflowRowProps>(
  BatchWorkflowRowComponent,
  (prev, next) =>
    prev.selected === next.selected &&
    // Referential compare: the parent memoizes the ordered/visible column array, so this only
    // differs when visibility or order actually changed.
    prev.columns === next.columns &&
    prev.showActions === next.showActions &&
    prev.showCheckbox === next.showCheckbox &&
    prev.reviewingBatchId === next.reviewingBatchId &&
    prev.onOpenWorkspace === next.onOpenWorkspace &&
    prev.onReviewSamples === next.onReviewSamples &&
    // Every batch field any column renders — a refetch produces a new batch object with equal
    // field values, so skipping render when all these match is the perf win of the memo.
    RENDER_AFFECTING_BATCH_FIELDS.every((f) => prev.batch[f] === next.batch[f])
);

export default BatchWorkflowRow;
